import { useEffect, useMemo } from 'react'
import { useAtom } from 'jotai'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'

import { useForYouChatsFeed } from '@renderer/overlay/hooks/use-running-chats'
import { useForYouDisplayChats, useForYouExecutedLinger } from '@renderer/overlay/hooks/for-you-linger'
import type { SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { useStagedActionDerivations, useVisibleStagedActions, type StagedAction } from '../../hooks/use-staged-actions'
import { actionBelongsToChat, forYouEmptyMessage, selectForYouActions, type ForYouFilterType } from './for-you-filter'
import { toForYouListRows, type ForYouListRow } from './for-you-list-rows'

const EMPTY_CHATS: Array<SidepanelChat> = []

/** Persisted so reopening the view lands on the list the user last chose. */
const forYouFilterAtom = atomWithGlobalStore<ForYouFilterType>('general', 'sidepanel.forYou.filter', 'staged')

export interface ForYouViewContext {
  initialFilter?: ForYouFilterType
}

export interface ForYouViewModel {
  filter: ForYouFilterType
  setFilter: (filter: ForYouFilterType) => void
  rows: Array<ForYouListRow>
  actionById: ReadonlyMap<string, StagedAction>
  feedKey: string
  isLoading: boolean
  isError: boolean
  emptyMessage: string
  fetchNextPage: () => Promise<unknown>
  isFetchingNextPage: boolean
}

export function useForYouViewModel(context?: ForYouViewContext): ForYouViewModel {
  const [filter, setFilter] = useAtom(forYouFilterAtom)
  const initialFilter = context?.initialFilter

  useEffect(() => {
    if (initialFilter) {
      setFilter(initialFilter)
    }
  }, [initialFilter, setFilter])

  const {
    data: chats,
    isLoading: chatsLoading,
    isError,
    feedKey,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useForYouChatsFeed(filter === 'archived' ? 'archived' : 'active')
  const displayChats = useForYouDisplayChats(chats, feedKey)
  const { allActions, isLoading: actionsLoading } = useVisibleStagedActions()
  const { getActionStatus } = useStagedActionDerivations()

  const showChats = filter !== 'completed'
  const showActions = filter !== 'staged'
  const visibleChats = showChats ? (filter === 'archived' ? chats : displayChats) : EMPTY_CHATS

  const filteredActions = useMemo(() => {
    if (!showActions) {
      return []
    }
    // A later chat page may own an action, so defer archived action rows until the chat list is complete.
    if (filter === 'archived' && hasNextPage) {
      return []
    }
    const selected = selectForYouActions(allActions, getActionStatus, filter === 'archived' ? 'archived' : 'completed')
    if (filter !== 'all' && filter !== 'archived') {
      return selected
    }
    return selected.filter((action) => !actionBelongsToChat(action, visibleChats))
  }, [allActions, visibleChats, filter, getActionStatus, hasNextPage, showActions])

  const actionById = useMemo(() => new Map(allActions.map((action) => [action.id, action])), [allActions])
  useForYouExecutedLinger(filter === 'archived' ? EMPTY_CHATS : visibleChats, feedKey, actionById)

  const rows = useMemo(
    () => toForYouListRows(visibleChats, filteredActions, showChats && hasNextPage),
    [filteredActions, hasNextPage, showChats, visibleChats],
  )

  const isLoading = showChats ? chatsLoading : actionsLoading
  const listIsEmpty = rows.length === 0

  return {
    filter,
    setFilter,
    rows,
    actionById,
    feedKey,
    isLoading,
    isError: showChats && isError,
    emptyMessage: listIsEmpty ? forYouEmptyMessage(filter) : '',
    fetchNextPage,
    isFetchingNextPage: showChats && isFetchingNextPage,
  }
}
